import { HoverCard } from "@mantine/core";
import clsx from "clsx";

import { mainPopUpPortalId } from "~/constants/portalIds.ts";
import type { ProjectRevenueBillingSplit } from "~/modules/project/api/projectRevenueReport/projectRevenueReportTypes.ts";
import {
	BILLED_AT_TARGET_COLOR,
	BILLED_BELOW_TARGET_COLOR,
	getBilledShare,
} from "~/modules/project/components/ProjectDetailsView/components/ProjectRevenueReportSection/projectRevenueReportUtils.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

type Props = {
	split: ProjectRevenueBillingSplit | undefined;
	size: number;
	ringWidth: number; // in px, independent of the size
};

export function ProjectRevenueSplitDonut({ split, size, ringWidth }: Props) {
	const totalCents = split ? split.onInvoiceCents + split.onQuotaCents + split.notBilledCents : 0;

	// Keeps the amounts of all rows aligned when a row has no billable money.
	if (!split || totalCents === 0) {
		return <span className="shrink-0"
					 style={{ width: size, height: size }} />;
	}

	const { onInvoiceCents, onQuotaCents, notBilledCents } = split;
	const { percentageLabel, isFullyBilled } = getBilledShare(split);

	// An SVG and not OverflowPercentageChart: at this size its faded arc mixes with the colour below
	// it, and its white centre shows on a hovered row. Here the billed and the not billed arcs never
	// overlap, the colours are flat, and the centre stays transparent.
	const center = size / 2;
	const radius = (size - ringWidth) / 2;
	const circumference = 2 * Math.PI * radius;
	const billedLength = circumference * ((onInvoiceCents + onQuotaCents) / totalCents);
	const notBilledLength = circumference - billedLength;

	return <HoverCard width={220}
					  withinPortal={true}
					  portalProps={{ target: "#" + mainPopUpPortalId }}
					  openDelay={0}
					  closeDelay={20}
					  shadow="md"
					  position="top">
		<HoverCard.Target>
			<div className="shrink-0 cursor-default">
				{/* The ring shows only billed and not billed. The hover card holds the full breakdown. */}
				<svg width={size}
					 height={size}
					 viewBox={`0 0 ${size} ${size}`}
					 className="block -rotate-90">
					{billedLength > 0 && <circle cx={center}
												 cy={center}
												 r={radius}
												 fill="none"
												 stroke={BILLED_AT_TARGET_COLOR}
												 strokeWidth={ringWidth}
												 strokeDasharray={`${billedLength} ${circumference}`} />}
					{notBilledLength > 0 && <circle cx={center}
													cy={center}
													r={radius}
													fill="none"
													stroke={BILLED_BELOW_TARGET_COLOR}
													strokeWidth={ringWidth}
													strokeDasharray={`${notBilledLength} ${circumference}`}
													strokeDashoffset={-billedLength} />}
				</svg>
			</div>
		</HoverCard.Target>
		<HoverCard.Dropdown className="rounded-md border border-gray-200 bg-white p-0 text-sm font-normal text-gray-700 shadow-md">
			<div className="flex items-baseline justify-between border-b border-gray-100 px-4 py-2">
				<span className="text-xs font-medium uppercase tracking-wide text-gray-500">Abgerechnet</span>
				<span className={clsx("font-semibold", isFullyBilled ? "text-success-600" : "text-red-600")}>
					{percentageLabel}%
				</span>
			</div>
			<div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 px-4 py-3">
				<div>Auf Rechnung</div>
				<div className="text-right">{formatCentsToCurrency(onInvoiceCents)}</div>
				{onQuotaCents !== 0 && <>
					<div>Auf Kontingent</div>
					<div className="text-right">{formatCentsToCurrency(onQuotaCents)}</div>
				</>}
				<div>Offen</div>
				<div className={clsx("text-right", notBilledCents !== 0 && "font-medium text-red-600")}>
					{formatCentsToCurrency(notBilledCents)}
				</div>
				<div className="col-span-2 my-1 h-px w-full bg-gray-200" />
				<div className="font-semibold text-gray-900">Erfassungen</div>
				<div className="text-right font-semibold text-gray-900">{formatCentsToCurrency(totalCents)}</div>
			</div>
		</HoverCard.Dropdown>
	</HoverCard>;
}
